import React, { useState, useEffect } from "react";
import apiClient from "../utils/apiClient";
import { X, Check, AlertCircle, Pencil, RefreshCw, Trash2 } from "lucide-react";
import server from "../utils/serverConfig";
import toast from "react-hot-toast";
import ExitSuccessModal from "./ExitSuccessModal";
import { useGlobalData } from "../hooks/useGlobalHooks";
import { LiveLTPCell } from "./OptimizedCell";

/**
 * BulkModifyModal Component
 * Handles single or bulk modification of existing orders.
 * Supports updating qty, limit price, and stop price.
 */
const BulkModifyModal = ({ isOpen, onClose, orders, onSuccess }) => {
    const { users } = useGlobalData();
    const [modifyData, setModifyData] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [submittedOrders, setSubmittedOrders] = useState([]);
    const [bulkFields, setBulkFields] = useState({
        limitPrice: "",
        stopPrice: "",
        qty: "",
        orderType: ""
    });

    const formatPrice = (p) => {
        if (p === undefined || p === null) return "-";
        return Number(p).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Initialize modify data when modal opens or orders change
    useEffect(() => {
        if (isOpen && orders && orders.length > 0) {
            setModifyData(orders.map(order => {
                let oType = order.orderType || order.order_type || order.type || order.OrderType;
                if (oType === 4 || oType === "4") oType = "STOPLIMIT";
                else if (oType === 3 || oType === "3") oType = "STOP";
                else if (oType === 2 || oType === "2") oType = "MARKET";
                else if (oType === 1 || oType === "1") oType = "LIMIT";
                const lp = Number(order.limit_price || order.limitPrice || 0);
                const sp = Number(order.stop_price || order.stopPrice || 0);

                let finalOType = String(oType || "").toUpperCase();
                if (!finalOType || finalOType === "LIMIT") {
                    if (lp > 0 && sp > 0) finalOType = "STOPLIMIT";
                    else finalOType = "LIMIT";
                }

                return {
                    orderId: order.order_id || order.orderId,
                    clientId: order.clientId || order.client_id,
                    broker: (order.broker || "fyers").toLowerCase(),
                    symbol: order.symbol,
                    strategy: order.strategy || order.order_tag || "Manual",
                    qty: order.qty || 0,
                    limitPrice: lp.toFixed(2),
                    stopPrice: sp.toFixed(2),
                    orderType: finalOType,
                    expectedQty: order.expectedQty,
                    status: 'pending' // pending, success, failed
                };
            }));
        }
    }, [isOpen, orders]);

    // Grouping logic: Organize orders by Symbol and Strategy
    const getGroupedData = () => {
        const groups = {};
        modifyData.forEach((item, index) => {
            const groupKey = `${item.symbol}|${item.strategy}`;
            if (!groups[groupKey]) {
                groups[groupKey] = {
                    symbol: item.symbol,
                    strategy: item.strategy,
                    items: []
                };
            }
            groups[groupKey].items.push({ ...item, actualIndex: index });
        });
        return Object.values(groups);
    };

    const handleGroupInputChange = (groupKey, field, value) => {
        const [symbol, strategy] = groupKey.split('|');
        const newData = [...modifyData];
        
        newData.forEach((item, index) => {
            if (item.symbol === symbol && item.strategy === strategy) {
                // Apply value and re-run logic for each item in group
                let updatedItem = { ...item, [field]: value };
                updatedItem = applyOrderLogic(updatedItem, field, value);
                newData[index] = updatedItem;
            }
        });
        
        setModifyData(newData);
        toast.success(`Updated all orders in ${strategy} | ${symbol}`);
    };

    const applyOrderLogic = (item, field, value) => {
        let newItem = { ...item };

        // Reset prices when switching order types manually
        if (field === "orderType") {
            if (value === "LIMIT") {
                newItem.stopPrice = "0.00";
            } else if (value === "MARKET") {
                newItem.limitPrice = "0.00";
                newItem.stopPrice = "0.00";
            } else if (value === "STOP") {
                newItem.limitPrice = "0.00";
            }
        }

        // Auto toggle orderType based on prices
        if (field === "limitPrice" || field === "stopPrice") {
            const lp = parseFloat(newItem.limitPrice) || 0;
            const sp = parseFloat(newItem.stopPrice) || 0;

            if (lp > 0 && sp === 0) {
                newItem.orderType = "LIMIT";
            } else if (lp > 0 && sp > 0) {
                newItem.orderType = "STOPLIMIT";
            }
        }

        return newItem;
    };

    const handleInputChange = (index, field, value) => {
        const newData = [...modifyData];
        newData[index] = applyOrderLogic(newData[index], field, value);
        newData[index][field] = value; // Apply the actual value after logic potentially pre-sets others

        // Re-run logic once more after the specific field is set to ensure invariants
        newData[index] = applyOrderLogic(newData[index], field, value);

        setModifyData(newData);
    };

    const handleRemoveItem = (index) => {
        setModifyData(prev => {
            const newData = prev.filter((_, i) => i !== index);
            if (newData.length === 0) {
                onClose();
            }
            return newData;
        });
        toast.success("Order removed from batch");
    };

    const applyBulk = (field) => {
        const val = bulkFields[field];
        if (val === "" || val === undefined) return;

        setModifyData(prev => prev.map(item => {
            const updated = { ...item, [field]: val };
            return applyOrderLogic(updated, field, val);
        }));
        toast.success(`Applied ${field} to all selected orders`);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const godseyePayload = [];
            const xtsPayload = [];
            const xtsAnuragPayload = [];

            modifyData.forEach(item => {
                const formatted = {
                    orderId: String(item.orderId),
                    clientId: String(item.clientId),
                    broker: item.broker,
                    qty: Number(item.qty),
                    limitPrice: Number(item.limitPrice),
                    stopPrice: Number(item.stopPrice),
                    orderType: item.orderType,
                    symbol: item.symbol
                };

                // Rely on global user store to find the actual broker, as an order might just say 'dhan_xts' instead of 'dhan_xts_anurag'
                const user = (users || []).find(u => String(u.client_id || u.clientId) === String(item.clientId));
                const broker = (user?.broker || item.broker || "").toLowerCase();

                if (broker === "dhan_xts" || broker === "dhan_ss" || broker === "xts" || broker === "dhan") {
                    xtsPayload.push(formatted);
                } else if (broker === "dhan_xts_anurag" || broker === "dhan_ss_anurag" || broker === "dhan_anurag") {
                    xtsAnuragPayload.push(formatted);
                } else {
                    godseyePayload.push(formatted);
                }
            });

            const requests = [];
            if (godseyePayload.length > 0) {
                requests.push(apiClient.post(`${server.godseye.baseUrl}modify-order`, godseyePayload));
            }
            if (xtsPayload.length > 0) {
                requests.push(apiClient.post(`${server.ss_dhan.baseUrl}modify-order-xts`, xtsPayload));
            }
            if (xtsAnuragPayload.length > 0) {
                requests.push(apiClient.post(`${server.ss_dhan_anurag.baseUrl}modify-order-xts`, xtsAnuragPayload));
            }

            if (requests.length === 0) return;

            const responses = await Promise.all(requests);

            let allResData = [];
            let allSuccess = true;
            let errorMessage = "";

            responses.forEach(res => {
                if (res.data.status === 200 || res.data.status === 207) {
                    allResData = [...allResData, ...(res.data.data || [])];
                } else {
                    allSuccess = false;
                    errorMessage = res.data.message || errorMessage || "Modification failed";
                }
            });

            if (allSuccess && allResData.length > 0) {
                const formattedOrders = allResData.map(o => {
                    const original = modifyData.find(m => String(m.orderId) === String(o.order_id));
                    return {
                        ...o,
                        orderId: o.order_id,
                        clientId: o.client_id,
                        status: o.status,
                        symbol: original?.symbol,
                        error: o.response?.message || o.message
                    };
                });

                if (onSuccess) onSuccess(allResData);

                setSubmittedOrders(formattedOrders);
                setSuccessModalOpen(true);
            } else if (allResData.length > 0) {
                // Partial success
                const formattedOrders = allResData.map(o => {
                    const original = modifyData.find(m => String(m.orderId) === String(o.order_id));
                    return {
                        ...o,
                        orderId: o.order_id,
                        clientId: o.client_id,
                        status: o.status,
                        symbol: original?.symbol,
                        error: o.response?.message || o.message
                    };
                });

                if (onSuccess) onSuccess(allResData);

                setSubmittedOrders(formattedOrders);
                setSuccessModalOpen(true);
                toast.error(errorMessage || "Some modifications failed");
            } else {
                toast.error(errorMessage || "Modification failed completely");
            }
        } catch (err) {
            console.error("❌ Modification error:", err);
            toast.error("Failed to modify orders: " + (err.response?.data?.message || err.message));
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    if (successModalOpen) {
        return (
            <ExitSuccessModal
                isOpen={true}
                onClose={() => {
                    setSuccessModalOpen(false);
                    onClose();
                }}
                orders={submittedOrders}
                actionType="Modify"
            />
        );
    }

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] backdrop-blur-sm p-4">
            <div className="bg-[#1a1d29] rounded-xl border border-gray-700 w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center bg-[#14161f]">
                    <div>
                        <h2 className="text-white text-xl font-bold">Modify Orders</h2>
                        <p className="text-gray-400 text-xs mt-1">Adjust quantity and prices for selected pending orders</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-full text-gray-400 transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Bulk Actions Bar */}
                {modifyData.length > 1 && (
                    <div className="bg-[#14161f]/50 px-6 py-3 border-b border-gray-700 flex flex-wrap items-center gap-6">
                        <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest">Bulk Edit:</span>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 font-medium whitespace-nowrap">Limit Price:</span>
                            <input
                                type="number" step="0.05" placeholder="0.00"
                                className="bg-[#0f1116] border border-gray-700 rounded px-2 py-1 text-xs text-white w-24 outline-none focus:border-blue-500"
                                value={bulkFields.limitPrice}
                                onChange={(e) => setBulkFields({ ...bulkFields, limitPrice: e.target.value })}
                            />
                            <button onClick={() => applyBulk('limitPrice')} className="bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white rounded transition-all p-1.5" title="Apply to all">
                                <Check className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 font-medium whitespace-nowrap">Type:</span>
                            <select
                                className="bg-[#0f1116] border border-gray-700 rounded px-2 py-1 text-xs text-white w-24 outline-none focus:border-blue-500"
                                value={bulkFields.orderType}
                                onChange={(e) => setBulkFields({ ...bulkFields, orderType: e.target.value })}
                            >
                                <option value="">Select</option>
                                <option value="LIMIT">LIMIT</option>
                                <option value="MARKET">MARKET</option>
                                <option value="STOP">STOP</option>
                                <option value="STOPLIMIT">STOP LIMIT</option>
                            </select>
                            <button onClick={() => applyBulk('orderType')} className="bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white rounded transition-all p-1.5" title="Apply to all">
                                <Check className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 font-medium whitespace-nowrap">Stop Price:</span>
                            <input
                                type="number" step="0.05" placeholder="0.00"
                                className="bg-[#0f1116] border border-gray-700 rounded px-2 py-1 text-xs text-white w-24 outline-none focus:border-blue-500"
                                value={bulkFields.stopPrice}
                                onChange={(e) => setBulkFields({ ...bulkFields, stopPrice: e.target.value })}
                            />
                            <button onClick={() => applyBulk('stopPrice')} className="bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white rounded transition-all p-1.5" title="Apply to all">
                                <Check className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 font-medium whitespace-nowrap">Qty:</span>
                            <input
                                type="number" placeholder="0"
                                className="bg-[#0f1116] border border-gray-700 rounded px-2 py-1 text-xs text-white w-20 outline-none focus:border-blue-500"
                                value={bulkFields.qty}
                                onChange={(e) => setBulkFields({ ...bulkFields, qty: e.target.value })}
                            />
                            <button onClick={() => applyBulk('qty')} className="bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white rounded transition-all p-1.5" title="Apply to all">
                                <Check className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Table Content */}
                <div className="flex-1 overflow-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#0f1117] sticky top-0 z-10 border-b border-gray-700">
                            <tr className="text-gray-400 uppercase text-[10px] font-bold">
                                <th className="text-left py-3 px-6">Order Details</th>
                                <th className="text-center py-3 px-2">LTP</th>
                                <th className="text-center py-3 px-2">Type</th>
                                <th className="text-center py-3 px-2">Qty</th>
                                <th className="text-center py-3 px-2">Limit Price</th>
                                <th className="text-center py-3 px-2">Stop Price</th>
                                <th className="text-center py-3 px-2 w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {getGroupedData().map((group, groupIdx) => (
                                <React.Fragment key={groupIdx}>
                                    {/* Group Header Row */}
                                    <tr className="bg-[#1c2230] border-y border-gray-700/50 group">
                                        <td className="py-2 px-6 border-l-4 border-blue-500">
                                            <div className="flex items-center gap-3">
                                                <div className="p-1.5 bg-blue-500/10 rounded animate-pulse">
                                                    <RefreshCw className="w-3 h-3 text-blue-400" />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-white font-bold text-xs tracking-tight uppercase flex items-center gap-2">
                                                        {group.strategy} 
                                                        <span className="h-1 w-1 bg-gray-600 rounded-full"></span>
                                                        <span className="text-gray-400 font-mono">{group.symbol}</span>
                                                    </span>
                                                    <span className="text-blue-400/60 text-[9px] font-bold uppercase tracking-wider">{group.items.length} Account{group.items.length > 1 ? 's' : ''} in Group</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-2 px-2 text-center">
                                            <div className="text-blue-400/60 text-[10px] font-mono italic">Sync Group:</div>
                                        </td>
                                        <td className="py-2 px-2">
                                            <select
                                                className="bg-[#0f1116] border border-blue-900/30 rounded px-1 py-1 text-[10px] text-center text-blue-400/90 outline-none focus:border-blue-500 block mx-auto cursor-pointer"
                                                onChange={(e) => handleGroupInputChange(`${group.symbol}|${group.strategy}`, 'orderType', e.target.value)}
                                                defaultValue=""
                                            >
                                                <option value="" disabled>SYNC</option>
                                                <option value="LIMIT">LIMIT</option>
                                                <option value="MARKET">MARKET</option>
                                                <option value="STOP">STOP</option>
                                                <option value="STOPLIMIT">STOPLIMIT</option>
                                            </select>
                                        </td>
                                        <td className="py-2 px-2">
                                            <input
                                                type="number"
                                                placeholder="ALL"
                                                className="w-16 bg-[#0f1116] border border-blue-900/30 rounded px-2 py-1 text-[10px] text-center text-blue-100 outline-none focus:border-blue-500 mx-auto block placeholder:text-blue-400/30"
                                                onChange={(e) => handleGroupInputChange(`${group.symbol}|${group.strategy}`, 'qty', e.target.value)}
                                            />
                                        </td>
                                        <td className="py-2 px-2">
                                            <input
                                                type="number" step="0.05"
                                                placeholder="ALL"
                                                className="w-20 bg-[#0f1116] border border-blue-900/30 rounded px-2 py-1 text-[10px] text-center text-blue-100 outline-none focus:border-blue-500 mx-auto block placeholder:text-blue-400/30"
                                                onChange={(e) => handleGroupInputChange(`${group.symbol}|${group.strategy}`, 'limitPrice', e.target.value)}
                                            />
                                        </td>
                                        <td className="py-2 px-2">
                                            <input
                                                type="number" step="0.05"
                                                placeholder="ALL"
                                                className="w-20 bg-[#0f1116] border border-blue-900/30 rounded px-1 py-1 text-[10px] text-center text-blue-100 outline-none focus:border-blue-500 mx-auto block placeholder:text-blue-400/30"
                                                onChange={(e) => handleGroupInputChange(`${group.symbol}|${group.strategy}`, 'stopPrice', e.target.value)}
                                            />
                                        </td>
                                        <td className="py-2 px-2"></td>
                                    </tr>

                                    {/* Individual Member Rows */}
                                    {group.items.map((item) => (
                                        <tr key={item.actualIndex} className="border-b border-gray-800/30 hover:bg-[#14161f]/30 transition-colors">
                                            <td className="py-3 px-6 pl-12">
                                                <div className="flex flex-col">
                                                    <span className="text-gray-400 font-mono text-[11px] font-bold">{item.clientId} ({item.broker.toUpperCase()})</span>
                                                    <span className="text-gray-600 text-[10px] font-mono mt-0.5">{item.orderId}</span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-2 text-center">
                                                <div className="text-gray-500 font-mono text-xs">
                                                    <LiveLTPCell symbol={item.symbol} formatFn={formatPrice} />
                                                </div>
                                            </td>
                                            <td className="py-3 px-2 text-center">
                                                <select
                                                    className="bg-[#0f1116] border border-gray-800 rounded px-1 py-1 text-xs text-center text-white/90 outline-none focus:border-blue-500 block mx-auto cursor-pointer"
                                                    value={item.orderType}
                                                    onChange={(e) => handleInputChange(item.actualIndex, 'orderType', e.target.value)}
                                                >
                                                    <option value="LIMIT">LIMIT</option>
                                                    <option value="MARKET">MARKET</option>
                                                    <option value="STOP">STOP</option>
                                                    <option value="STOPLIMIT">STOPLIMIT</option>
                                                </select>
                                            </td>
                                            <td className="py-3 px-2">
                                                <input
                                                    type="number"
                                                    className="w-20 bg-[#0f1116] border border-gray-800 rounded px-2 py-1 text-xs text-center text-white outline-none focus:border-blue-500 mx-auto block"
                                                    value={item.qty}
                                                    onChange={(e) => handleInputChange(item.actualIndex, 'qty', e.target.value)}
                                                />
                                            </td>
                                            <td className="py-3 px-2">
                                                <input
                                                    type="number" step="0.05"
                                                    className="w-24 bg-[#0f1116] border border-gray-800 rounded px-2 py-1 text-xs text-center text-white outline-none focus:border-blue-500 mx-auto block"
                                                    value={item.limitPrice}
                                                    onChange={(e) => handleInputChange(item.actualIndex, 'limitPrice', e.target.value)}
                                                />
                                            </td>
                                            <td className="py-3 px-2">
                                                <input
                                                    type="number" step="0.05"
                                                    className="w-24 bg-[#0f1116] border border-gray-800 rounded px-2 py-1 text-xs text-center text-white outline-none focus:border-blue-500 mx-auto block"
                                                    value={item.stopPrice}
                                                    onChange={(e) => handleInputChange(item.actualIndex, 'stopPrice', e.target.value)}
                                                />
                                            </td>
                                            <td className="py-3 px-2 text-center">
                                                <button
                                                    onClick={() => handleRemoveItem(item.actualIndex)}
                                                    className="p-1.5 text-gray-600 hover:text-red-500 hover:bg-red-500/10 rounded transition-all"
                                                    title="Remove from batch"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-700 flex justify-between items-center bg-[#14161f]">
                    <div className="text-gray-500 text-xs flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-orange-400" />
                        <span>Changes will reflect in broker systems immediately.</span>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 rounded-lg text-gray-300 hover:bg-gray-800 font-medium transition-colors"
                        >Cancel</button>
                        <button
                            onClick={handleSubmit}
                            disabled={submitting || modifyData.length === 0}
                            className="px-8 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold transition-all shadow-lg shadow-blue-900/40 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {submitting ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : <Pencil className="w-4 h-4" />}
                            Update {modifyData.length} Order{modifyData.length > 1 ? 's' : ''}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BulkModifyModal;
